import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from "react";
import Tile from "./Tile";
import Cell from "./Cell";
import { Board } from "../../helper";
import useEvent from "../../hooks/useEvent";
import GameOverlay from "./GameOverlay";
import swipeSound from "../../assets/sounds/swipe.mp3";
import loseSound from "../../assets/sounds/2048lose.mp3";
import level1Sound from "../../assets/sounds/2048level1.mp3";
import level2Sound from "../../assets/sounds/2048level2.mp3";
import level3Sound from "../../assets/sounds/2048level3.mp3";
import "./2048.scss";
import "./styles.scss";
import { useLanguage } from "../../contexts/LanguageContext";

// 컴포넌트 외부로 이동하여 재사용
const swipeAudio = new Audio(swipeSound);
const loseAudio = new Audio(loseSound);
const level1Audio = new Audio(level1Sound);
const level2Audio = new Audio(level2Sound);
const level3Audio = new Audio(level3Sound);

// 이미지 프리로드 함수는 컴포넌트 외부에 유지
const preloadImages = () => {
  const numbers = [2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048, 4096];
  numbers.forEach(num => {
    const img = new Image();
    img.src = require(`../../assets/images/${num}.png`);
  });
};

// 오디오 컨텍스트 생성 함수
const createAudioContext = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  return new AudioContext();
};

const BoardView = forwardRef((props, ref) => {
  const [board, setBoard] = useState(new Board());
  const [keepPlaying, setKeepPlaying] = useState(false);
  const [hasUsedContinue, setHasUsedContinue] = useState(false);
  const audioContext = useRef(null);
  const gainNode = useRef(null);
  const [isSoundMuted, setIsSoundMuted] = useState(false);
  const { t } = useLanguage();

  // 이미지 프리로드 useEffect를 컴포넌트 내부로 이동
  useEffect(() => {
    preloadImages();
  }, []);

  // 오디오 컨텍스트 초기화
  useEffect(() => {
    audioContext.current = createAudioContext();
    gainNode.current = audioContext.current.createGain();
    gainNode.current.connect(audioContext.current.destination);
    gainNode.current.gain.value = 0.3; // 효과음 볼륨을 30%로 설정
    
    return () => {
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, []);

  const playSound = async (audioFile) => {
    if (!isSoundMuted) {
      try {
        const response = await fetch(audioFile);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.current.decodeAudioData(arrayBuffer);
        
        const source = audioContext.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(gainNode.current);
        source.start(0);
      } catch (error) {
        console.error('효과음 재생 실패:', error);
      }
    }
  };

  const checkAndPlaySound = (newBoard) => {
    const newTiles = newBoard.tiles.filter(tile => tile.isNew());
    
    newTiles.forEach(tile => {
      if (tile.value === 4096) {
        playSound(level3Sound);
      } else if (tile.value === 2048) {
        playSound(level3Sound);
      } else if (tile.value === 512 || tile.value === 1024) {
        playSound(level2Sound);
      } else if (tile.value === 128 || tile.value === 256) {
        playSound(level1Sound);
      }
    });

    if (newBoard.hasLost()) {
      playSound(loseSound);
    }
  };

  useImperativeHandle(ref, () => ({
    resetGame: () => {
      setBoard(new Board());
      setHasUsedContinue(false);
    }
  }));

  const handleKeyDown = (event) => {
    if (board.hasWon() && !keepPlaying) {
      return;
    }

    if (board.hasLost()) {
      return;
    }

    let direction;
    switch (event.key.toLowerCase()) {
      case 'a':
        direction = 0;
        break;
      case 'd':
        direction = 2;
        break;
      case 'w':
        direction = 1;
        break;
      case 's':
        direction = 3;
        break;
      default:
        return;
    }

    let boardClone = Object.assign(
      Object.create(Object.getPrototypeOf(board)),
      board
    );
    let newBoard = boardClone.move(direction);
    
    if (newBoard !== null) {
      playSound(swipeSound);
      checkAndPlaySound(newBoard);
      setBoard(newBoard);
    }

    if (newBoard && newBoard.hasLost()) {
      setBoard(newBoard);
    }
  };

  useEvent("keydown", handleKeyDown);

  const cells = React.useMemo(() => {
    return board.cells.map((row, rowIndex) => {
      return (
        <div key={rowIndex}>
          {row.map((col, colIndex) => {
            return <Cell key={rowIndex * board.size + colIndex} />;
          })}
        </div>
      );
    });
  }, [board.cells]);

  const tiles = React.useMemo(() => {
    return board.tiles
      .filter((tile) => tile.value !== 0)
      .map((tile, index) => {
        return <Tile tile={tile} key={index} />;
      });
  }, [board.tiles]);

  const resetGame = () => {
    const newBoard = new Board();
    setBoard(newBoard);
    setHasUsedContinue(false);
  };

  const handleContinueAfterAd = () => {
    if (hasUsedContinue) return;
    const boardClone = Object.assign(
      Object.create(Object.getPrototypeOf(board)),
      board
    );
    if (boardClone.removeLowestTiles()) {
      setBoard(boardClone);
      setHasUsedContinue(true);
    }
  };

  const handleButtonClick = (direction) => {
    if (board.hasWon() && !keepPlaying) {
      return;
    }

    if (board.hasLost()) {
      return;
    }
    
    let boardClone = Object.assign(
      Object.create(Object.getPrototypeOf(board)),
      board
    );
    let newBoard = boardClone.move(direction);
    
    if (newBoard !== null) {
      playSound(swipeSound);
      checkAndPlaySound(newBoard);
      setBoard(newBoard);
    }

    if (newBoard && newBoard.hasLost()) {
      setBoard(newBoard);
    }
  };

  // 메모리 정리를 위한 useEffect 추가
  useEffect(() => {
    const cleanupAnimations = () => {
      const tiles = document.querySelectorAll('.tile');
      tiles.forEach(tile => {
        // 이미 완료된 애니메이션의 클래스만 제거
        if (!tile.classList.contains('isMoving')) {
          tile.classList.remove('merged');
        }
      });
    };

    // 애니메이션이 완료된 후에만 클린업 실행
    const cleanupTimeout = setTimeout(cleanupAnimations, 200);

    return () => {
      clearTimeout(cleanupTimeout);
    };
  }, [board]); // board가 변경될 때만 실행

  // 기존 하드웨어 가속 최적화 유지
  useEffect(() => {
    const boardElement = document.querySelector('.board');
    if (boardElement) {
      boardElement.style.transform = 'translate3d(0,0,0)';
      boardElement.style.backfaceVisibility = 'hidden';
      boardElement.style.perspective = '1000';
      
      const tiles = document.querySelectorAll('.tile');
      tiles.forEach(tile => {
        tile.style.transform = 'translate3d(0,0,0)';
        tile.style.backfaceVisibility = 'hidden';
      });
    }
  }, [board]);

  return (
    <div className="game2048">
      <div className="details-box">
        <div className="score-box">
          <span className="score-header">Score : </span>
          <span>{board.score}</span>
        </div>
        <div className="sound-control">
          <span className="sound-label">
            {t('games.common.sound')}
          </span>
          <button 
            className="sound-toggle"
            onClick={() => setIsSoundMuted(!isSoundMuted)}
          >
            {isSoundMuted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>
      <div className="board">
        {cells}
        {tiles}
        <GameOverlay
          onRestart={resetGame}
          board={board}
          onKeepGoing={() => setKeepPlaying(true)}
          keepPlaying={keepPlaying}
          onContinueAfterAd={handleContinueAfterAd}
          hasUsedContinue={hasUsedContinue}
        />
      </div>
      <div className="control-buttons">
        <div className="control-row">
          <button 
            className="control-button"
            onClick={() => handleButtonClick(1)} // down으로 변경
          >
            ↑
          </button>
        </div>
        <div className="control-row">
          <button 
            className="control-button"
            onClick={() => handleButtonClick(0)} // left
          >
            ←
          </button>
          <button 
            className="control-button"
            onClick={() => handleButtonClick(3)} // up으로 변경
          >
            ↓
          </button>
          <button 
            className="control-button"
            onClick={() => handleButtonClick(2)} // right
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
});

export default BoardView;